import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { ApiResponse, BackendResponse } from "../models/api-response";
import { getLogger } from "src/utils/logger";
import { GENERIC_ERROR_MESSAGE } from "src/utils/errorMessages";

const logger = getLogger('external-api.service');

/**
 * Makes an API call to an external service
 *
 * @param options The request configuration
 * @returns A promise resolving to an ApiResponse containing the data or an error
 */
export const callExternalApi = async <T = BackendResponse>(options: {
  config: AxiosRequestConfig;
}): Promise<ApiResponse<T>> => {
  try {
    const logConfig = { ...options.config };
    if (logConfig.headers && logConfig.headers.Authorization) {
      logConfig.headers = {
        ...logConfig.headers,
        Authorization: 'Bearer [TOKEN HIDDEN]'
      };
    }
    
    logger.debug("API Request:", logConfig);
    const response: AxiosResponse = await axios(options.config);
    logger.debug("API Response status:", response.status);
    
    // Handle direct response (API Gateway passes through Lambda response)
    const responseData = response.data as T;
    // Log response data safely without risking circular references
    logger.debug(`Response received with status: ${(responseData as any).status}`);
    
    return {
      data: responseData,
      error: null,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      const { response } = axiosError;
      
      if (response?.data) {
        try {
          // Try to parse error response
          const errorResponse = typeof response.data === 'string'
            ? JSON.parse(response.data)
            : response.data;

          // Backend routes are split between a `message` key and an `error`
          // key for the human-readable reason, so read both before falling
          // back — otherwise every route using `error` (document storage,
          // usage limits) surfaces only the generic string and the caller's
          // specific message is lost.
          const detail = [errorResponse.message, errorResponse.error].find(
            (value) => typeof value === 'string' && value.trim() !== ''
          );

          return {
            data: null,
            error: {
              message: detail || GENERIC_ERROR_MESSAGE
            },
          };
        } catch (parseError) {
          logger.error("Failed to parse error response:", parseError);
        }
      }
      
      logger.error('External API call failed', error);
      return {
        data: null,
        error: { message: GENERIC_ERROR_MESSAGE },
      };
    } else {
      logger.error('External API call failed', error);
      return {
        data: null,
        error: { message: GENERIC_ERROR_MESSAGE },
      };
    }
  }
};

export type { ApiResponse };
